import { createContext, useEffect, useState, version } from "react";
import { localStorageGet, localStorageRemove, localStorageSave } from "../../../services/localStorageService";
import { createInterchangeability, deleteInterchangeability, getInterhcangeabilities } from "../services/InterchangeabilityService";
import { useSnackBar } from "../../../hooks/useSnackBar";

const InterchangeabilityContext = createContext();



const entryMapper = (item) => [item.id, item];
const draftMapper = (item, draft) => draft ? mapper(draft) : mapper(item);
const mapper = (item) => ({ ...item, PN: item.targetPartNumber ?? null, "Conditional Notes": item.conditionalNote })

const localStoragePrefix = "configuration/parts/draft/engineering/interchangeability";

const InterchangeabilityProvider = ({ children, part }) => {

  const [interchangeItems, setInterchangeItems] = useState([]);
  const [idxItems, setIdxItems] = useState({});
  const [idxBackup, setIdxBackup] = useState({});
  const [drafts, setDrafts] = useState(getInterchangeDrafts());
  const [defaults, setDefaults] = useState({});
  const [newDrafts, setNewDrafts] = useState([]);
  const { successMsg, infoMsg, warningMsg } = useSnackBar();
  const [opened, setOpened] = useState(null);
  const isUnderEdition = (item) => item.id.toString() in drafts;

  const itemOpened = (item) => opened === item.id;

  useEffect(() => {
    loadInterchangeabilities(part);
  }, [part]);

  const loadInterchangeabilities = (part) => {
    getInterhcangeabilities(part.id).then(response => {
      if (response.data.responseCode === 200) {
        // local storage array items
        let draftsArray = localStorageGet(localStoragePrefix, { partId: part.id.toString() });
        // indexed local storage items
        let drafts = Object.fromEntries(draftsArray.map(entryMapper));
        // mergedItems (no new drafts) indexed 
        let mergedItems = Object.fromEntries(response.data.load.map((item) => draftMapper(item, drafts[item.id])).map(entryMapper));
        // new drafts array
        let newDraftsArray = draftsArray.filter(draft => !(draft.id in mergedItems));
        setNewDrafts(newDraftsArray);
        mergedItems = { ...Object.fromEntries(newDraftsArray.map(entryMapper)), ...mergedItems }

        setIdxItems(mergedItems);
        setDefaults(mergedItems);
        setIdxBackup(Object.fromEntries(response.data.load.map(entryMapper)));
        setInterchangeItems(response.data.load);
      }
    });
  }

  const startEditing = (item) => {
    // setDefaults(prev => ({ ...prev, [item.id]: { targetPartId: item.targetPartId } }));
    setDrafts(prev => ({ ...prev, [item.id.toString()]: { ...(prev[item.id.toString()] ?? {}), "form": item } }));
    localStorageSave(localStoragePrefix + "/form", { id: item.id.toString(), partId: part.id.toString() }, item);
    setOpened(item.id);
  }

  const deleteItem = (itemId) => {
    // Dialog
    deleteInterchangeability(itemId).then(response => {
      if (response.data.responseCode === 200){
        localStorageRemove(localStoragePrefix, { id: itemId.toString(), partId: part.id.toString() });
        successMsg("Interchangeability was successfully deleted.");
        setInterchangeItems(prev => prev.filter(item => item.id !== itemId));
        setIdxItems(prev => {
          let copy = {...prev};
          delete copy[itemId];
          setDefaults(copy);
          return copy;
        });
        setIdxBackup(prev => {
          let copy = {...prev};
          delete copy[itemId];
          return copy;
        });
      } else {
        warningMsg(response.data.message || "An error occurred while deleting the interchangeability.")
      }
    }).catch(error => warningMsg("An error occurred while deleting the interchangeability."));

  }

  const saveItem = (itemId, removeDraft = true) => {
    let updatedItem = idxItems[itemId];
    let itemToSave = {
      id: updatedItem.id,
      sourcePartId: idxItems[itemId].sourcePartId ?? part.id,
      targetPartId: updatedItem.targetPartId,
      bothWays: updatedItem.bothWays ?? false,
      conditionalNote: updatedItem.conditionalNote,
      version: updatedItem.version,
      override: updatedItem.override ?? false
    }
    infoMsg("Saving...")
    createInterchangeability(part.id, itemToSave).then(response => {
      if (response.data.responseCode === 200) {
        successMsg("Interchangeability saved successfully");
        response.data.load.forEach(interchange => {
        })
        if (removeDraft) setDrafts(prev => {
          let copy = { ...prev };
          delete copy[itemId.toString()];
          localStorageRemove(localStoragePrefix, { id: itemId.toString(), partId: part.id.toString() });
          setOpened(null);
          return copy;
        });
        loadInterchangeabilities(part);
      }
    })
  }

  const addItem = () => {
    infoMsg("Creating draft", 2000);
    createInterchangeability(part.id, { id: null }).then(response => {
      if (response.data.responseCode === 200) {
        setIdxItems(prev => ({ ...prev, [response.data.load.id]: mapper(response.data.load) }));
        setNewDrafts(prev => [response.data.load, ...prev]);
        startEditing(response.data.load);
      }
    })
  }

  const changeItem = (itemId, newObject) => {
    setIdxItems(prev => {
      localStorageSave(localStoragePrefix + "/form", { id: itemId.toString(), partId: part.id.toString() }, { ...prev[itemId], ...newObject });
      return ({ ...prev, [itemId]: { ...prev[itemId], ...newObject } })
    })
  }

  useEffect(() => {
  }, [idxItems]);

  const cancelEdition = (item) => {
    // takebackup
    if (idxBackup[item.id]) {
      setIdxItems(prev => ({ ...prev, [item.id]: mapper(idxBackup[item.id]) }));
      setDefaults(prev => ({ ...prev, [item.id]: mapper(idxBackup[item.id]) }));
    } else {
      infoMsg('Draft discarded');
      setIdxItems(prev => {
        let copy = {...prev};
        delete copy[item.id];
        return copy;
      });
      setDefaults(prev => {
        let copy = {...prev};
        delete copy[item.id];
        return copy;}
      )
      setNewDrafts(prev => prev.filter(i => i.id !== item.id)); 
    }
    setDrafts(prev => {
      let copy = { ...prev };
      delete copy[item.id.toString()];
      localStorageRemove(localStoragePrefix, { id: item.id.toString() });
      return copy;
    });
    setOpened(null);
  }

  const tryOpen = (item) => {
    if (isUnderEdition(item)) setOpened(prev => prev === item.id ? null : item.id);
  }

  return (
    <InterchangeabilityContext.Provider value={{ interchangeItems, isUnderEdition, startEditing, saveItem, addItem, idxItems, changeItem, defaults, cancelEdition, part, newDrafts, itemOpened, tryOpen, deleteItem }}>
      {children}
    </InterchangeabilityContext.Provider>
  )
}

export { InterchangeabilityContext, InterchangeabilityProvider };

const getInterchangeDrafts = () => {
  return [...Array(localStorage.length).keys()].map(i => localStorage.key(i))
    .filter(key => key.startsWith(localStoragePrefix)).reduce((acc, key) => {
      const queryParams = new URLSearchParams(key.split('?')[1]);
      if (localStorage.getItem('u_id') === queryParams.get('userId')) {
        let id = queryParams.get("id");
        if (!acc[id]) {
          acc[id] = {};
        }
        let item = localStorage.getItem(key);
        // return item ? ({...acc, [id]:{...acc[id], [key.split("?")[0].replace(localStoragePrefix + '/', "")]: JSON.parse(item)}}) : acc;
        // acc[id].add(key.split("?")[0].replace(localStoragePrefix + '/', ""));
        return acc;
      }
      return acc;
    }, {});
}